import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Shield, BarChart3, Wrench, Megaphone, ExternalLink } from "lucide-react";
import { useConsent } from "@/contexts/ConsentContext";
import { translations, DEFAULT_CONSENT } from "@/config/consent";
import { toast } from "sonner";

interface CookiePreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CookiePreferencesModal = ({ open, onOpenChange }: CookiePreferencesModalProps) => {
  const { consent, setConsent, locale } = useConsent();
  const t = translations[locale].modal;

  const [preferences, setPreferences] = useState<{
    essential: true;
    analytics: boolean;
    product_improvement: boolean;
    marketing: boolean;
  }>({
    essential: true,
    analytics: false,
    product_improvement: false,
    marketing: false,
  });

  useEffect(() => {
    if (consent) {
      setPreferences({
        essential: true,
        analytics: consent.choices.analytics,
        product_improvement: consent.choices.product_improvement,
        marketing: consent.choices.marketing || false,
      });
    } else {
      setPreferences({
        essential: true,
        analytics: false,
        product_improvement: false,
        marketing: false,
      });
    }
  }, [consent, open]);

  const handleSave = () => {
    setConsent({
      version: consent?.version || DEFAULT_CONSENT.version,
      choices: preferences,
      timestamp: new Date().toISOString(),
      locale,
    });
    toast.success(t.successToast);
    onOpenChange(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: {
      essential: true;
      analytics: boolean;
      product_improvement: boolean;
      marketing: boolean;
    } = {
      essential: true as const,
      analytics: true,
      product_improvement: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    setConsent({
      version: consent?.version || DEFAULT_CONSENT.version,
      choices: {
        essential: true,
        analytics: true,
        product_improvement: true,
        marketing: true,
      },
      timestamp: new Date().toISOString(),
      locale,
    });
    toast.success(t.successToast);
    onOpenChange(false);
  };

  const handleRejectAll = () => {
    const allRejected: {
      essential: true;
      analytics: boolean;
      product_improvement: boolean;
      marketing: boolean;
    } = {
      essential: true as const,
      analytics: false,
      product_improvement: false,
      marketing: false,
    };
    setPreferences(allRejected);
    setConsent({
      version: consent?.version || DEFAULT_CONSENT.version,
      choices: {
        essential: true,
        analytics: false,
        product_improvement: false,
        marketing: false,
      },
      timestamp: new Date().toISOString(),
      locale,
    });
    toast.success(t.successToast);
    onOpenChange(false);
  };

  const categories = [
    {
      id: "essential" as const,
      icon: Shield,
      name: t.categories.essential.name,
      description: t.categories.essential.description,
      locked: true,
    },
    {
      id: "analytics" as const,
      icon: BarChart3,
      name: t.categories.analytics.name,
      description: t.categories.analytics.description,
      locked: false,
    },
    {
      id: "product_improvement" as const,
      icon: Wrench,
      name: t.categories.product_improvement.name,
      description: t.categories.product_improvement.description,
      locked: false,
    },
    // Marketing category hidden by default - can be enabled later
    // {
    //   id: "marketing" as const,
    //   icon: Megaphone,
    //   name: t.categories.marketing.name,
    //   description: t.categories.marketing.description,
    //   locked: false,
    // },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{t.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {categories.map((category, index) => {
            const Icon = category.icon;
            return (
              <div key={category.id}>
                <div className="flex items-start justify-between gap-4 py-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Icon className="w-5 h-5 mt-1 text-primary" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={category.id} className="text-base font-medium cursor-pointer">
                          {category.name}
                        </Label>
                        {category.locked && (
                          <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                            Requis
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                  <Switch
                    id={category.id}
                    checked={preferences[category.id]}
                    onCheckedChange={(checked) => {
                      if (!category.locked) {
                        setPreferences((prev) => ({ ...prev, [category.id]: checked }));
                      }
                    }}
                    disabled={category.locked}
                    aria-label={category.name}
                  />
                </div>
                {index < categories.length - 1 && <Separator />}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
          <a
            href="/privacy"
            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.privacyPolicy}
            <ExternalLink className="w-3 h-3" />
          </a>
          <span>•</span>
          <a
            href="/cookies"
            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.cookiePolicy}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleRejectAll} className="w-full sm:w-auto">
            {t.rejectAll}
          </Button>
          <div className="flex gap-2 flex-1">
            <Button onClick={handleSave} className="flex-1">
              {t.save}
            </Button>
            <Button onClick={handleAcceptAll} className="flex-1">
              {t.acceptAll}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
